import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users, MapPin, Store, Trash2, Search,
  UserCheck, AlertCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { staffAPI, assignmentsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ListPageHeader from '../../components/shared/ListPageHeader';

const StaffAssignments = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [cities, setCities] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [showCityDialog, setShowCityDialog] = useState(false);
  const [showRetailerDialog, setShowRetailerDialog] = useState(false);
  const [includeExisting, setIncludeExisting] = useState(false);
  const [activeTab, setActiveTab] = useState('by-staff');
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchStaff(),
      fetchCities(),
      fetchRetailers()
    ]);
    setLoading(false);
  };

  const fetchStaff = async () => {
    try {
      const { data } = await staffAPI.list();
      setStaff(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff'));
    }
  };

  const fetchCities = async () => {
    try {
      const { data } = await assignmentsAPI.getCities();
      setCities(data.cities || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load cities'));
    }
  };

  const fetchRetailers = async () => {
    try {
      const { data } = await assignmentsAPI.getRetailers();
      setRetailers(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load retailers'));
    }
  };

  const handleAssignCity = async () => {
    if (!selectedStaff || !selectedCity) {
      toast.error('Please select both staff and city');
      return;
    }

    try {
      const { data } = await assignmentsAPI.assignCityToStaff({
        staff_id: selectedStaff,
        city: selectedCity,
        include_existing: includeExisting,
      });
      toast.success(`Successfully assigned ${data.retailers_assigned} retailers from ${selectedCity} to staff`);
      setShowCityDialog(false);
      setSelectedStaff('');
      setSelectedCity('');
      setIncludeExisting(false);
      fetchRetailers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to assign city'));
    }
  };

  const handleAssignRetailers = async () => {
    if (!selectedStaff || selectedRetailers.length === 0) {
      toast.error('Please select staff and at least one retailer');
      return;
    }

    try {
      const { data } = await assignmentsAPI.assignRetailersToStaff({
        staff_id: selectedStaff,
        party_ids: selectedRetailers,
      });
      toast.success(`Successfully assigned ${data.retailers_assigned} retailers to staff`);
      setShowRetailerDialog(false);
      setSelectedStaff('');
      setSelectedRetailers([]);
      fetchRetailers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to assign retailers'));
    }
  };

  const handleRemoveAssignment = (staffMember, retailer) => {
    setConfirmModal({
      title: 'Remove Assignment',
      description: `Remove ${retailer.name} from ${staffMember.name}'s assigned retailers?`,
      confirmLabel: 'Remove',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await assignmentsAPI.removeAssignment(staffMember.id, retailer.id);
          toast.success('Assignment removed successfully');
          fetchRetailers();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to remove assignment'));
        }
      },
    });
  };

  const toggleRetailerSelection = (retailerId) => {
    setSelectedRetailers(prev =>
      prev.includes(retailerId)
        ? prev.filter(id => id !== retailerId)
        : [...prev, retailerId]
    );
  };

  const getRetailersByStaff = (staffId) => {
    return retailers.filter(r => r.assigned_staff_ids && r.assigned_staff_ids.includes(staffId));
  };

  const getUnassignedRetailers = () => {
    return retailers.filter(r => !r.assigned_staff_ids || r.assigned_staff_ids.length === 0);
  };

  const filteredRetailersForDialog = retailers.filter(r => {
    const matchesSearch = !searchTerm ||
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.phone?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCity = !filterCity || filterCity === 'all' || r.city === filterCity;

    return matchesSearch && matchesCity;
  });

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const totalAssigned = retailers.filter(r => r.assigned_staff_ids && r.assigned_staff_ids.length > 0).length;
  const unassignedCount = getUnassignedRetailers().length;

  return (
    <div className="h-full flex flex-col" data-testid="staff-assignments-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Staff Assignments"
        stats={[
          { icon: Users, label: 'Total Staff', value: staff.length, color: 'slate' },
          { icon: Store, label: 'Total Retailers', value: retailers.length, color: 'emerald' },
          { icon: UserCheck, label: 'Assigned', value: totalAssigned, color: 'blue' },
          { icon: AlertCircle, label: 'Unassigned', value: unassignedCount, color: 'amber' },
        ]}
        ctaLabel="Assign Retailers"
        onCtaClick={() => setShowRetailerDialog(true)}
        secondaryAction={{
          label: 'Assign City',
          icon: MapPin,
          onClick: () => setShowCityDialog(true)
        }}
      />

      {/* Tabs Section */}
      <div className="px-2 pt-2">
        <div className="flex items-center gap-1 bg-white p-1 rounded-lg shadow-sm border border-slate-200/60 w-fit">
          <button
            onClick={() => setActiveTab('by-staff')}
            className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all ${
              activeTab === 'by-staff'
                ? 'bg-teal-600 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            By Staff
          </button>
          <button
            onClick={() => setActiveTab('by-city')}
            className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all ${
              activeTab === 'by-city'
                ? 'bg-teal-600 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            By City
          </button>
          <button
            onClick={() => setActiveTab('unassigned')}
            className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-all ${
              activeTab === 'unassigned'
                ? 'bg-teal-600 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Unassigned
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading assignments...</p>
              </div>
            </div>
          ) : (
            <>
              {/* By Staff View */}
              {activeTab === 'by-staff' && (
                <div className="space-y-3">
                  {staff.length === 0 ? (
                    <div className="bg-white rounded-md border border-slate-200/60 shadow-sm text-center py-16">
                      <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Users className="w-7 h-7 text-slate-400" />
                      </div>
                      <p className="text-[15px] font-medium text-slate-600">No staff members found</p>
                      <p className="text-[13px] text-slate-400 mt-1">Add staff members first to assign retailers</p>
                    </div>
                  ) : (
                    staff.map((staffMember, index) => {
                      const assignedRetailers = getRetailersByStaff(staffMember.id);
                      return (
                        <div
                          key={staffMember.id}
                          className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden animate-tableRowFadeIn"
                          style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}
                        >
                          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staffMember.name)}`}>
                                {getInitials(staffMember.name)}
                              </div>
                              <div>
                                <h3 className="text-[14px] font-semibold text-slate-800">{staffMember.name}</h3>
                                <p className="text-[12px] text-slate-500">{staffMember.role_label || 'Field Staff'} -- {staffMember.phone}</p>
                              </div>
                            </div>
                            <span className="px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full text-[12px] font-medium">
                              {assignedRetailers.length} Retailers
                            </span>
                          </div>
                          <div className="p-4">
                            {assignedRetailers.length === 0 ? (
                              <div className="text-center py-6 text-slate-500 text-[13px]">
                                No retailers assigned yet
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                {assignedRetailers.map(retailer => (
                                  <div key={retailer.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${getInitialColor(retailer.name)}`}>
                                        {getInitials(retailer.name)}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="font-medium text-[12px] text-slate-800 truncate">{retailer.name}</div>
                                        <div className="text-[10px] text-slate-500 truncate">
                                          {retailer.city || 'No city'}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => handleRemoveAssignment(staffMember, retailer)}
                                      className="p-1 hover:bg-rose-100 rounded transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* By City View */}
              {activeTab === 'by-city' && (
                <div className="space-y-3">
                  {cities.length === 0 ? (
                    <div className="bg-white rounded-md border border-slate-200/60 shadow-sm text-center py-16">
                      <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <MapPin className="w-7 h-7 text-slate-400" />
                      </div>
                      <p className="text-[15px] font-medium text-slate-600">No cities found</p>
                      <p className="text-[13px] text-slate-400 mt-1">Add retailers with city information to see them here</p>
                    </div>
                  ) : (
                    cities.map((city, index) => {
                      const cityRetailers = retailers.filter(r => r.city === city.city);
                      return (
                        <div
                          key={city.city}
                          className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden animate-tableRowFadeIn"
                          style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}
                        >
                          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-white">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                                <MapPin className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div>
                                <h3 className="text-[14px] font-semibold text-slate-800">{city.city}</h3>
                                <p className="text-[12px] text-slate-500">{city.state}</p>
                              </div>
                            </div>
                            <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[12px] font-medium">
                              {cityRetailers.length} Retailers
                            </span>
                          </div>
                          <div className="p-4">
                            <div className="space-y-2">
                              {cityRetailers.map(retailer => (
                                <div key={retailer.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold ${getInitialColor(retailer.name)}`}>
                                      {getInitials(retailer.name)}
                                    </div>
                                    <span className="font-medium text-[12px] text-slate-800">{retailer.name}</span>
                                  </div>
                                  {retailer.assigned_staff_ids && retailer.assigned_staff_ids.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {retailer.assigned_staff_ids.map(staffId => {
                                        const assignedStaff = staff.find(s => s.id === staffId);
                                        return assignedStaff ? (
                                          <span key={staffId} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-[10px] font-medium">
                                            {assignedStaff.name}
                                          </span>
                                        ) : null;
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-amber-600 font-medium">Unassigned</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Unassigned View */}
              {activeTab === 'unassigned' && (
                <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
                    <h3 className="text-[14px] font-semibold text-slate-800">Unassigned Retailers</h3>
                  </div>
                  <div className="p-4">
                    {getUnassignedRetailers().length === 0 ? (
                      <div className="text-center py-10">
                        <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                          <UserCheck className="w-7 h-7 text-emerald-600" />
                        </div>
                        <p className="text-[15px] font-medium text-slate-600">All retailers assigned!</p>
                        <p className="text-[13px] text-slate-400 mt-1">Great job! All retailers have been assigned to staff members.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                        {getUnassignedRetailers().map((retailer, index) => (
                          <div
                            key={retailer.id}
                            className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg animate-tableRowFadeIn"
                            style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold ${getInitialColor(retailer.name)}`}>
                                {getInitials(retailer.name)}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-[12px] text-slate-800 truncate">{retailer.name}</div>
                                <div className="text-[10px] text-slate-500">
                                  {retailer.city}{retailer.state ? `, ${retailer.state}` : ''}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assign City Dialog */}
      <Dialog open={showCityDialog} onOpenChange={setShowCityDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Entire City to Staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-slate-700">Select Staff</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.role_label || 'Staff'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-slate-700">Select City</Label>
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(city => (
                    <SelectItem key={city.city} value={city.city}>
                      {city.city} ({city.retailer_count} retailers)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-existing"
                checked={includeExisting}
                onChange={(e) => setIncludeExisting(e.target.checked)}
                className="rounded border-slate-300"
              />
              <Label htmlFor="include-existing" className="text-[13px] text-slate-600 cursor-pointer">
                Include retailers already assigned to other staff
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignCity} className="bg-teal-600 hover:bg-teal-700">
              Assign City
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Retailers Dialog */}
      <Dialog open={showRetailerDialog} onOpenChange={setShowRetailerDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Assign Specific Retailers to Staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 flex-1 overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-slate-700">Select Staff</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.role_label || 'Staff'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search retailers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
              </div>
              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city.city} value={city.city}>
                      {city.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
              {filteredRetailersForDialog.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-[13px]">
                  No retailers found
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredRetailersForDialog.map(retailer => (
                    <div
                      key={retailer.id}
                      className="p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => toggleRetailerSelection(retailer.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedRetailers.includes(retailer.id)}
                          onChange={() => {}}
                          className="rounded border-slate-300"
                        />
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${getInitialColor(retailer.name)}`}>
                          {getInitials(retailer.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[13px] text-slate-800">{retailer.name}</div>
                          <div className="text-[11px] text-slate-500">
                            {retailer.city}{retailer.state ? `, ${retailer.state}` : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedRetailers.length > 0 && (
              <div className="text-[13px] text-slate-600 bg-teal-50 px-3 py-2 rounded-lg">
                Selected: <span className="font-semibold text-teal-700">{selectedRetailers.length}</span> retailer(s)
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRetailerDialog(false);
              setSelectedRetailers([]);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAssignRetailers} disabled={selectedRetailers.length === 0} className="bg-teal-600 hover:bg-teal-700">
              Assign {selectedRetailers.length} Retailer(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default StaffAssignments;
